// Verbesserte ReviewSection-Komponente mit Fehlerbehandlung und besserer UX
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    List, Avatar, Rate, Form, Input, Button, 
    Alert, Empty, Typography, Space, Divider, message, Tooltip, Modal, Pagination
} from 'antd';
import { UserOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { createReview, fetchReviewsByShop, updateReview, deleteReview } from '../services/reviewService';
import { isAuthenticated, getCurrentUser } from '../services/authService';
// import './ReviewSection.css'; // Remove CSS import

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Helper function to format date (consider using a library like date-fns or moment later for relative time)
const formatDate = (dateString) => {
    try {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        const date = new Date(dateString);
        if (isNaN(date.getTime())) { // Check if date is valid
             return 'Unbekanntes Datum';
        }
        return date.toLocaleDateString('de-DE', options);
    } catch (e) {
        return dateString || 'Unbekanntes Datum';
    }
};

const ReviewSection = ({ shopId, reviews: initialReviews = [], onReviewAdded }) => {
    const [reviews, setReviews] = useState(initialReviews);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [user, setUser] = useState(null);
    const [authenticated, setAuthenticated] = useState(false);
    const [form] = Form.useForm(); // Form instance

    // Load user data
    useEffect(() => {
        const loadUserData = async () => {
            try {
                const authStatus = await isAuthenticated();
                setAuthenticated(authStatus);
                if (authStatus) {
                    const currentUser = await getCurrentUser();
                    setUser(currentUser);
                    // Pre-fill form if user is logged in and form instance exists
                    // Don't pre-fill author here, it's handled in initialValues or backend logic
                    // if (currentUser?.name && form) {
                    //      form.setFieldsValue({ author: currentUser.name });
                    // }
                }
            } catch (err) {
                console.error('Fehler beim Laden der Benutzerdaten:', err);
            }
        };
        loadUserData();
    }, [form]); // Add form dependency

    // Handle form submission using Form's onFinish
    const onFinish = async (values) => {
        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const reviewData = {
                shop: shopId,
                rating: values.rating,
                text: values.text,
                // Wenn NICHT authentifiziert, nimm den Autor aus dem Formular.
                // Wenn authentifiziert, wird der Autor im Backend aus req.user gesetzt.
                author: !authenticated ? values.author : undefined, 
            };

            const response = await createReview(reviewData);

            if (response.success && response.data) {
                // Unterscheide die Nachricht basierend darauf, ob der Benutzer eingeloggt war
                const successMessage = authenticated 
                    ? 'Vielen Dank für Ihre Bewertung!' 
                    : 'Vielen Dank! Ihre Bewertung wurde zur Prüfung eingereicht.';
                message.success(successMessage);

                // Nur genehmigte (also von eingeloggten Usern stammende) Reviews direkt anzeigen
                if (response.data.isApproved) {
                    const newReviewData = { ...response.data, createdAt: new Date().toISOString() }; 
                    setReviews(prevReviews => [newReviewData, ...prevReviews]);
                    if (onReviewAdded) {
                        onReviewAdded(newReviewData);
                    }
                }
                
                form.resetFields(); // Reset form fields regardless of approval
                // Setze initiale Bewertung zurück, falls das Formular zurückgesetzt wird
                form.setFieldsValue({ rating: 5 });

            } else {
                setSubmitError(response.message || 'Bewertung konnte nicht gespeichert werden.');
            }
        } catch (err) {
            console.error('Fehler beim Erstellen der Bewertung:', err);
            let userMessage = err.message || 'Bewertung konnte nicht gespeichert werden.';
            // Die Backend-Fehlermeldung für 'bereits bewertet' ist spezifischer
            // if (err.message?.includes('bereits bewertet')) { 
            //     userMessage = 'Sie haben diesen Shop bereits bewertet.';
            // }
            setSubmitError(userMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onFinishFailed = (errorInfo) => {
        console.log('Form Validation Failed:', errorInfo);
        message.error('Bitte überprüfen Sie Ihre Eingaben.');
    };

    return (
        <div className="review-section-antd"> 
            <Divider />
            <Title level={4} style={{ marginBottom: '20px' }}>Bewertungen ({reviews.length})</Title>

            {/* Reviews List using List.Item and List.Item.Meta */}
            {reviews.length > 0 ? (
                <List
                    className="comment-list"
                    itemLayout="horizontal"
                    dataSource={reviews}
                    renderItem={item => (
                        <List.Item
                            key={item.id || item._id}
                            actions={[
                                <Tooltip title={formatDate(item.createdAt)}> 
                                    <span>{formatDate(item.createdAt)}</span> 
                                </Tooltip>
                             ]}
                        >
                            <List.Item.Meta
                                avatar={<Avatar icon={<UserOutlined />} />} // Placeholder avatar
                                title={<Text strong>{item.author}</Text>}
                                description={
                                    <>
                                        <Rate disabled value={item.rating} style={{ fontSize: 14, marginBottom: '5px'}} />
                                        <Paragraph style={{ margin: 0 }}>{item.text}</Paragraph>
                                    </>
                                }
                            />
                        </List.Item>
                    )}
                />
            ) : (
                <Empty description="Noch keine Bewertungen vorhanden." style={{ marginBottom: '20px' }}/>
            )}

            <Divider />

            {/* Review Form */}
            <Title level={4} style={{ marginBottom: '10px' }}>Bewertung schreiben</Title>

            {submitError && (
                <Alert message={submitError} type="error" showIcon style={{ marginBottom: '15px' }} closable onClose={() => setSubmitError(null)} />
            )}

            {!authenticated && (
                 <Alert 
                    message={<>Sie schreiben die Bewertung als Gast. Ihre Bewertung wird nach Prüfung durch einen Admin veröffentlicht. <Link to="/login">Anmelden</Link> oder <Link to="/register">Registrieren</Link>?</>}
                    type="info" 
                    showIcon 
                    style={{ marginBottom: '15px' }} 
                 />
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                // Initialwert für Author nur setzen, wenn NICHT authentifiziert
                initialValues={{ rating: 5, text: '', author: '' }}
            >
                 {/* Zeige das Autor-Feld nur für Gäste an */}
                 {!authenticated && (
                    <Form.Item
                        name="author"
                        label="Ihr Name (als Gast)"
                        rules={[{ required: true, message: 'Bitte geben Sie Ihren Namen ein.', whitespace: true }]}
                    >
                        <Input placeholder="Max Mustermann" disabled={isSubmitting} />
                    </Form.Item>
                 )}

                 {/* Authentifizierte Benutzer sehen ihren Namen (optional) */}
                 {authenticated && user && (
                     <p>Sie schreiben diese Bewertung als: <strong>{user.name}</strong></p>
                 )}

                <Form.Item
                    name="rating"
                    label="Ihre Bewertung"
                    rules={[{ required: true, message: 'Bitte wählen Sie eine Bewertung aus.' }]}
                >
                    {/* Set allowClear={false} if a rating must be submitted */}
                    <Rate allowClear={false} disabled={isSubmitting} /> 
                </Form.Item>

                <Form.Item
                    name="text"
                    label="Ihre Erfahrung"
                    rules={[
                        { required: true, message: 'Bitte beschreiben Sie Ihre Erfahrung.', whitespace: true },
                        { min: 10, message: 'Die Bewertung sollte mindestens 10 Zeichen lang sein.' },
                        { max: 1000, message: 'Die Bewertung darf maximal 1000 Zeichen lang sein.' }
                    ]}
                >
                    <TextArea 
                        rows={4} 
                        placeholder="Wie war Ihr Einkauf? Was hat Ihnen gefallen oder nicht gefallen?" 
                        disabled={isSubmitting} 
                    />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={isSubmitting}>
                        Bewertung absenden
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ReviewSection;